import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { TrendingUp, ArrowUp, Sparkles, Info, Loader2 } from "lucide-react";
import { API, formatCount } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";

const WINDOWS = [
  { id: 3, label: "3h" },
  { id: 6, label: "6h" },
  { id: 12, label: "12h" },
  { id: 24, label: "24h" },
];

/**
 * What is climbing, as opposed to what is already at the top.
 *
 * The main chart rewards videos that are already big. This one is built from the
 * stored hourly snapshots and ranks by movement instead: how many places a video
 * gained, and how fast it is picking up views. It costs no YouTube quota because
 * every figure comes from data already on disk.
 */
export const RisingPage = () => {
  const { country } = useCountry();
  const [hours, setHours] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/rising`, {
          params: { country: country.code, hours, limit: 30 },
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ videos: [], notice: "Could not load the rising feed." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [country.code, hours]);

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="rising-page">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-heading text-2xl sm:text-3xl font-black flex items-center gap-3 mb-2">
          <TrendingUp className="w-7 h-7 text-orange-500" />
          Rising Now
        </h1>
        <p className="text-sm text-white/50 mb-6">
          Climbing fastest in {country.name} — measured against our own hourly snapshots,
          not YouTube's ranking.
        </p>

        <div className="flex items-center gap-2 mb-6">
          <span className="text-xs text-white/40 mr-1">Compare against:</span>
          {WINDOWS.map((w) => (
            <button
              key={w.id}
              onClick={() => setHours(w.id)}
              className={`px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-full text-xs font-medium transition-colors ${
                hours === w.id
                  ? "bg-orange-500 text-black"
                  : "bg-white/5 border border-white/10 text-white/60 hover:bg-white/10"
              }`}
              data-testid={`rising-window-${w.id}`}
            >
              {w.label} ago
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : (
          <>
            {data?.notice && (
              <div className="flex items-start gap-2 mb-6 px-4 py-3 rounded-xl bg-orange-500/10 border border-orange-500/30">
                <Info className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-white/70">{data.notice}</p>
              </div>
            )}

            {data?.videos?.length > 0 && (
              <p className="text-xs text-white/40 mb-4">
                Movement over the last {data.window_hours}h.
              </p>
            )}

            <div className="space-y-2">
              {data?.videos?.map((video, index) => (
                <motion.div
                  key={video.video_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.3) }}
                >
                  <Link
                    to={`/video/${video.video_id}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-orange-500/40 transition-colors group"
                    data-testid={`rising-item-${video.video_id}`}
                  >
                    <span className="w-8 text-center font-heading font-bold text-white/30 shrink-0">
                      {index + 1}
                    </span>

                    {video.thumbnail_url && (
                      <img
                        src={video.thumbnail_url}
                        alt=""
                        loading="lazy"
                        className="w-24 sm:w-32 aspect-video object-cover rounded-lg shrink-0"
                      />
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold line-clamp-2 group-hover:text-orange-400 transition-colors">
                        {video.title}
                      </p>
                      <p className="text-xs text-white/40 truncate">{video.channel_title}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px]">
                        {video.is_new_on_chart ? (
                          <span className="inline-flex items-center gap-1 text-yellow-400">
                            <Sparkles className="w-3 h-3" /> New at #{video.rank}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-green-400">
                            <ArrowUp className="w-3 h-3" />
                            {video.rank_change} places — now #{video.rank}
                          </span>
                        )}
                        {video.views_per_hour > 0 && (
                          <span className="text-white/40">
                            {formatCount(video.views_per_hour)}/hr
                          </span>
                        )}
                        {video.view_count_raw > 0 && (
                          <span className="text-white/30">
                            {formatCount(video.view_count_raw)} views
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
